import React, { useState, useEffect, useCallback } from 'react';
import './App.css';

// ── Error boundary ────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{padding:'40px',fontFamily:'monospace',color:'#ff4757',background:'#0a0c10',minHeight:'100vh'}}>
          <div style={{marginBottom:'8px',fontSize:'14px',fontWeight:'bold'}}>Application Error</div>
          <pre style={{fontSize:'12px',color:'#8892a4',whiteSpace:'pre-wrap'}}>{this.state.error.toString()}</pre>
          <button onClick={() => this.setState({ error: null })} style={{marginTop:'16px',padding:'8px 16px',background:'#1e2330',border:'1px solid #2a3040',color:'#e8eaf0',cursor:'pointer',borderRadius:'4px',fontFamily:'monospace'}}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export { ErrorBoundary };

const API = process.env.REACT_APP_API_URL || '/api';

// ── Auth token management ─────────────────────────────────────────────────────
// Token lives in memory only. Intentionally not persisted to localStorage.

const TOKEN_KEY = 'gcp_cm_token';

let _token = null;

function setToken(t) {
  _token = t;
  try { localStorage.setItem(TOKEN_KEY, t); } catch {}
}

function getToken() {
  if (_token) return _token;
  try {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) { _token = stored; return _token; }
  } catch {}
  return null;
}

function clearToken() {
  _token = null;
  try { localStorage.removeItem(TOKEN_KEY); } catch {}
}

async function apiFetch(url, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
  return fetch(url, { ...options, headers });
}

// ── Login screen ──────────────────────────────────────────────────────────────

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Login failed');
        return;
      }
      onLogin(data.token);
    } catch (err) {
      setError('Could not reach server');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-overlay">
      <div className="login-card">
        <div className="login-logo">GCP</div>
        <h1 className="login-title">Cluster Manager</h1>
        <p className="login-sub">Sign in to continue</p>
        {error && <div className="login-error">{error}</div>}
        <form onSubmit={handleSubmit} className="login-form">
          <label>
            Username
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </label>
          <button
            type="submit"
            className="btn btn-start login-submit"
            disabled={loading || !username || !password}
          >
            {loading ? <span className="spinner-small" /> : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

const STATUS_COLOR = {
  RUNNING: '#00e5a0',
  STOPPED: '#ff4757',
  PARTIAL: '#ffa502',
  UNKNOWN: '#747d8c',
  ERROR: '#ff4757',
};

const STATUS_LABEL = {
  RUNNING: 'Running',
  STOPPED: 'Stopped',
  PARTIAL: 'Partial',
  UNKNOWN: 'Unknown',
  ERROR: 'Error',
};

function StatusDot({ status }) {
  const color = STATUS_COLOR[status] || STATUS_COLOR.UNKNOWN;
  return (
    <span className="status-dot-wrap">
      <span className="status-dot" style={{ background: color }} />
      <span className="status-label" style={{ color }}>{STATUS_LABEL[status] || status}</span>
    </span>
  );
}

function ClusterCard({ cluster, onAction, debugMode }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/clusters/${cluster.id}/status${debugMode ? '?debug=true' : ''}`;
      const res = await apiFetch(url);
      const data = await res.json();
      setStatus(data);
    } catch {
      setStatus({ status: 'ERROR', error: 'Could not reach API' });
    } finally {
      setLoading(false);
    }
  }, [cluster.id, debugMode]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  async function handleAction(action) {
    setActionLoading(action);
    try {
      await apiFetch(`${API}/clusters/${cluster.id}/${action}`, { method: 'POST' });
      setTimeout(fetchStatus, 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  }

  const isRunning = status?.status === 'RUNNING';
  const isStopped = status?.status === 'STOPPED';

  return (
    <div className={`cluster-card ${expanded ? 'expanded' : ''}`}>
      <div className="cluster-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="cluster-card-title">
          <span className="cluster-name">{cluster.displayName}</span>
          <span className="cluster-meta">{cluster.projectId} / {cluster.region}</span>
        </div>
        <div className="cluster-card-status">
          {loading ? (
            <span className="spinner-small" />
          ) : (
            <StatusDot status={status?.status || 'UNKNOWN'} />
          )}
          <span className="expand-icon">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      <div className="cluster-card-actions">
        <button
          className="btn btn-start"
          onClick={() => handleAction('start')}
          disabled={actionLoading !== null || isRunning}
        >
          {actionLoading === 'start' ? <span className="spinner-small" /> : '▶'} Start
        </button>
        <button
          className="btn btn-stop"
          onClick={() => handleAction('stop')}
          disabled={actionLoading !== null || isStopped}
        >
          {actionLoading === 'stop' ? <span className="spinner-small" /> : '■'} Stop
        </button>
        <button className="btn btn-refresh" onClick={fetchStatus} disabled={loading}>
          ↻ Refresh
        </button>
        <button className="btn btn-edit" onClick={() => onAction('edit', cluster)}>
          ✎ Edit
        </button>
        <button className="btn btn-delete" onClick={() => onAction('delete', cluster)}>
          ✕ Remove
        </button>
      </div>

      {expanded && status && (
        <div className="cluster-details">
          <div className="detail-grid">
            {cluster.clusterMode === 'gke-autopilot' ? (
              <>
                <div className="detail-item">
                  <span className="detail-label">Workloads</span>
                  <span className="detail-value">{status.workloads?.length ?? '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Running Pods</span>
                  <span className="detail-value">{status.runningPods ?? '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Total Pods</span>
                  <span className="detail-value">{status.totalPods ?? '—'}</span>
                </div>
              </>
            ) : (
              <>
                <div className="detail-item">
                  <span className="detail-label">Node Pools</span>
                  <span className="detail-value">{status.nodePoolCount ?? '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Total Nodes</span>
                  <span className="detail-value">{status.totalNodes ?? '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">VMs</span>
                  <span className="detail-value">{status.vmCount ?? '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Running VMs</span>
                  <span className="detail-value">{status.runningVMs ?? '—'}</span>
                </div>
              </>
            )}
          </div>

          {cluster.schedule && (
            <div className="schedule-info">
              <span className="detail-label">Schedule</span>
              <div className="schedule-badges">
                <span className={`badge ${cluster.schedule.shutdownEnabled ? 'badge-red' : 'badge-gray'}`}>
                  Shutdown: {cluster.schedule.shutdownEnabled ? cluster.schedule.shutdownCron : 'off'}
                </span>
                <span className={`badge ${cluster.schedule.startupEnabled ? 'badge-green' : 'badge-gray'}`}>
                  Startup: {cluster.schedule.startupEnabled ? cluster.schedule.startupCron : 'off'}
                </span>
                <span className="badge badge-gray">{cluster.schedule.timezone}</span>
                <span className={`badge ${cluster.clusterMode === 'vm-pool' ? 'badge-orange' : cluster.clusterMode === 'gke-autopilot' ? 'badge-green' : 'badge-blue'}`}>
                  {cluster.clusterMode === 'vm-pool' ? 'VM Pool' : cluster.clusterMode === 'gke-autopilot' ? 'Autopilot' : 'GKE Standard'}
                </span>
                {cluster.openshift?.enabled && (
                  <span className="badge badge-red">OpenShift</span>
                )}
              </div>
            </div>
          )}

          {status.vms && status.vms.length > 0 && cluster.clusterMode === 'vm-pool' && (
            <div className="pool-list">
              <span className="detail-label">Matched VMs ({status.vms.length})</span>
              <div className="pool-row" style={{fontFamily:'var(--font-mono)',fontSize:'10px',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:'1px solid var(--border)',paddingBottom:'4px'}}>
                <span style={{flex:2}}>Name</span>
                <span style={{flex:1}}>Zone</span>
                <span>Status</span>
              </div>
              {status.vms.map(vm => (
                <div key={`${vm.zone}-${vm.name}`} className="pool-row">
                  <span className="pool-name" style={{flex:2}}>{vm.name}</span>
                  <span style={{color:'var(--text-muted)',fontSize:'11px',flex:1}}>{vm.zone}</span>
                  <span style={{color: vm.status === 'RUNNING' ? 'var(--green)' : vm.status === 'TERMINATED' ? 'var(--red)' : 'var(--orange)',fontSize:'11px'}}>{vm.status}</span>
                </div>
              ))}
            </div>
          )}

          {status.nodePools && status.nodePools.length > 0 && (
            <div className="pool-list">
              <span className="detail-label">Node Pools</span>
              {status.nodePools.map(pool => (
                <div key={pool.name} className="pool-row">
                  <span className="pool-name">{pool.name}</span>
                  <span className="pool-nodes">{pool.nodeCount} nodes</span>
                  <span className="pool-status">{pool.status}</span>
                </div>
              ))}
            </div>
          )}

          {status.workloads?.length > 0 && (
            <div className="pool-list">
              <span className="detail-label">Workloads</span>
              <div className="pool-row" style={{fontFamily:'var(--font-mono)',fontSize:'10px',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:'1px solid var(--border)',paddingBottom:'4px'}}>
                <span style={{flex:2}}>Name</span>
                <span style={{flex:1}}>Namespace</span>
                <span style={{flex:1}}>Kind</span>
                <span>Ready</span>
              </div>
              {status.workloads.map(w => (
                <div key={`${w.namespace}/${w.name}`} className="pool-row">
                  <span className="pool-name" style={{flex:2}}>{w.name}</span>
                  <span style={{color:'var(--text-muted)',fontSize:'11px',flex:1}}>{w.namespace}</span>
                  <span style={{color:'var(--blue)',fontSize:'11px',flex:1}}>{w.kind}</span>
                  <span style={{color: w.ready >= w.desired && w.desired > 0 ? 'var(--green)' : w.ready === 0 ? 'var(--red)' : 'var(--orange)'}}>{w.ready}/{w.desired}</span>
                </div>
              ))}
            </div>
          )}

          {status.error && (
            <div className="error-banner">{status.error}</div>
          )}

          {debugMode && status.debug && (
            <div className="debug-panel">
              <div className="debug-title">Debug Info</div>
              <div className="debug-grid">
                <div className="debug-row">
                  <span className="debug-label">Mode</span>
                  <span className="debug-value">{status.debug.mode}</span>
                </div>
                <div className="debug-row">
                  <span className="debug-label">Pattern</span>
                  <span className="debug-value">{status.debug.pattern}</span>
                </div>
                {status.debug.regex && (
                  <div className="debug-row">
                    <span className="debug-label">Regex</span>
                    <span className="debug-value">{status.debug.regex}</span>
                  </div>
                )}
                <div className="debug-row">
                  <span className="debug-label">GCP Filter</span>
                  <span className="debug-value">{status.debug.filter}</span>
                </div>
                <div className="debug-row">
                  <span className="debug-label">Zones Scanned</span>
                  <span className="debug-value">{status.debug.zonesScanned?.length > 0 ? status.debug.zonesScanned.join(', ') : 'none'}</span>
                </div>
              </div>
              {status.debug.allVmsScanned?.length > 0 ? (
                <div className="debug-vm-table">
                  <div className="debug-vm-header">
                    <span>VM Name</span>
                    <span>Zone</span>
                    <span>Status</span>
                    <span>Matched</span>
                  </div>
                  {status.debug.allVmsScanned.map((vm, i) => (
                    <div key={i} className={`debug-vm-row ${vm.matched ? 'matched' : 'unmatched'}`}>
                      <span>{vm.name}</span>
                      <span>{vm.zone}</span>
                      <span>{vm.status}</span>
                      <span>{vm.matched ? '✓' : '✗'}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="debug-empty">No VMs scanned. Check that the project ID and region are correct and Workload Identity has compute.viewer access.</div>
              )}
              {status.debug.error && (
                <pre className="debug-stack">{status.debug.error}</pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const GCP_REGIONS = [
  'us-central1', 'us-east1', 'us-east4', 'us-east5', 'us-south1',
  'us-west1', 'us-west2', 'us-west3', 'us-west4',
  'northamerica-northeast1', 'northamerica-northeast2',
  'southamerica-east1', 'southamerica-west1',
  'europe-central2', 'europe-north1', 'europe-southwest1',
  'europe-west1', 'europe-west2', 'europe-west3', 'europe-west4',
  'europe-west6', 'europe-west8', 'europe-west9', 'europe-west10', 'europe-west12',
  'asia-east1', 'asia-east2', 'asia-northeast1', 'asia-northeast2', 'asia-northeast3',
  'asia-south1', 'asia-south2', 'asia-southeast1', 'asia-southeast2',
  'australia-southeast1', 'australia-southeast2',
  'me-central1', 'me-central2', 'me-west1',
  'africa-south1',
];

const TIMEZONES = [
  { label: 'US - Central (Chicago)',        value: 'America/Chicago' },
  { label: 'US - Eastern (New York)',        value: 'America/New_York' },
  { label: 'US - Mountain (Denver)',         value: 'America/Denver' },
  { label: 'US - Pacific (Los Angeles)',     value: 'America/Los_Angeles' },
  { label: 'US - Alaska (Anchorage)',        value: 'America/Anchorage' },
  { label: 'US - Hawaii (Honolulu)',         value: 'Pacific/Honolulu' },
  { label: 'UTC',                            value: 'UTC' },
  { label: 'Europe - London',               value: 'Europe/London' },
  { label: 'Europe - Paris / Berlin',       value: 'Europe/Paris' },
  { label: 'Europe - Helsinki',             value: 'Europe/Helsinki' },
  { label: 'Asia - Dubai',                  value: 'Asia/Dubai' },
  { label: 'Asia - Kolkata',                value: 'Asia/Kolkata' },
  { label: 'Asia - Singapore',              value: 'Asia/Singapore' },
  { label: 'Asia - Tokyo',                  value: 'Asia/Tokyo' },
  { label: 'Asia - Shanghai',               value: 'Asia/Shanghai' },
  { label: 'Australia - Sydney',            value: 'Australia/Sydney' },
  { label: 'Australia - Perth',             value: 'Australia/Perth' },
  { label: 'Brazil - Sao Paulo',            value: 'America/Sao_Paulo' },
];

function ResourcePreview({ projectId, region, pattern, clusterMode }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function search() {
    if (!projectId || !region || !pattern) {
      setError('Project ID, region, and name pattern are all required to search.');
      return;
    }
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const params = new URLSearchParams({ projectId, region, pattern, clusterMode });
      const res = await apiFetch(`${API}/preview/resources?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const isVmPool = clusterMode === 'vm-pool';

  return (
    <div className="resource-preview">
      <div className="resource-preview-header">
        <span className="detail-label">{isVmPool ? 'VM Pattern Preview' : 'Cluster Lookup'}</span>
        <button
          className="btn btn-preview"
          onClick={search}
          disabled={loading || !projectId || !region || !pattern}
          title="Search for matching resources"
        >
          {loading ? <span className="spinner-small" /> : '🔍'} {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {error && <div className="preview-error">{error}</div>}

      {results && results.type === 'vms' && (
        <div className="preview-results">
          <div className="preview-summary">
            <span className={results.matched.length > 0 ? 'preview-count-ok' : 'preview-count-none'}>
              {results.matched.length} VM{results.matched.length !== 1 ? 's' : ''} matched
            </span>
            <span className="preview-scanned">
              (scanned {results.totalScanned} VMs across {results.zonesScanned.length} zones)
            </span>
            {results.regex && <span className="preview-regex">regex: {results.regex}</span>}
          </div>
          {results.matched.length > 0 && (
            <div className="preview-table">
              <div className="preview-header-row">
                <span>Name</span><span>Zone</span><span>Status</span>
              </div>
              {results.matched.map(vm => (
                <div key={`${vm.zone}-${vm.name}`} className="preview-row">
                  <span className="preview-name">{vm.name}</span>
                  <span>{vm.zone}</span>
                  <span style={{color: vm.status === 'RUNNING' ? 'var(--green)' : vm.status === 'TERMINATED' ? 'var(--red)' : 'var(--orange)'}}>{vm.status}</span>
                </div>
              ))}
            </div>
          )}
          {results.matched.length === 0 && (
            <div className="preview-empty">No VMs matched. Check your pattern and project ID.</div>
          )}
        </div>
      )}

      {results && results.type === 'gke' && (
        <div className="preview-results">
          {results.error && <div className="preview-error">{results.error}</div>}
          {results.matched.length > 0 && results.matched.map(c => (
            <div key={c.name} className="preview-gke-result">
              <div className="preview-summary">
                <span className="preview-count-ok">✓ Cluster found: {c.name}</span>
                <span className="preview-scanned">{c.location} · {c.currentNodeCount} nodes · {c.status}</span>
              </div>
              {c.nodePools?.length > 0 && (
                <div className="preview-table">
                  <div className="preview-header-row">
                    <span>Node Pool</span><span>Nodes</span><span>Status</span>
                  </div>
                  {c.nodePools.map(p => (
                    <div key={p.name} className="preview-row">
                      <span className="preview-name">{p.name}</span>
                      <span>{p.nodeCount}</span>
                      <span>{p.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {results.matched.length === 0 && !results.error && (
            <div className="preview-empty">Cluster not found. Check the cluster name and region.</div>
          )}
        </div>
      )}
    </div>
  );
}

function ClusterForm({ cluster, onSave, onCancel, isNewFromExplorer }) {
  const isEdit = !!cluster && !isNewFromExplorer;
  const [form, setForm] = useState(cluster ? {
    ...cluster,
    schedule: {
      shutdownCron: '0 17 * * *',
      shutdownEnabled: true,
      startupCron: '0 8 * * 1-5',
      startupEnabled: false,
      timezone: 'America/Chicago',
      ...cluster.schedule,
    },
    openshift: {
      enabled: false,
      masterPattern: 'master',
      infraPattern: 'infra',
      workerPattern: 'worker',
      drainTimeoutSeconds: 300,
      apiUrl: '',
      token: '',
      ...cluster.openshift,
    },
  } : {
    id: '',
    displayName: '',
    projectId: '',
    clusterName: '',
    region: 'us-central1',
    clusterMode: 'gke',
    schedule: {
      shutdownCron: '0 17 * * *',
      shutdownEnabled: true,
      startupCron: '0 8 * * 1-5',
      startupEnabled: false,
      timezone: 'America/Chicago',
    },
    openshift: {
      enabled: false,
      masterPattern: 'master',
      infraPattern: 'infra',
      workerPattern: 'worker',
      drainTimeoutSeconds: 300,
      apiUrl: '',
      token: '',
    },
    tags: [],
  });

  const [error, setError] = useState(null);

  function update(path, value) {
    setError(null);
    setForm(prev => {
      const next = { ...prev };
      const parts = path.split('.');
      if (parts.length === 2) {
        next[parts[0]] = { ...next[parts[0]], [parts[1]]: value };
      } else if (parts.length === 3) {
        next[parts[0]] = { ...next[parts[0]], [parts[1]]: { ...next[parts[0]]?.[parts[1]], [parts[2]]: value } };
      } else {
        next[path] = value;
      }
      return next;
    });
  }

  function validate() {
    if (!form.id.trim()) return 'ID is required';
    if (!form.displayName.trim()) return 'Display Name is required';
    if (!form.projectId.trim()) return 'GCP Project ID is required';
    if (!form.clusterName.trim()) return (form.clusterMode === 'vm-pool' ? 'VM Label / Cluster Name is required' : 'Cluster Name is required');
    if (!form.region) return 'Region is required';
    if (form.schedule.shutdownEnabled && !form.schedule.shutdownCron.trim()) return 'Shutdown cron is required when shutdown is enabled';
    if (form.schedule.startupEnabled && !form.schedule.startupCron.trim()) return 'Startup cron is required when startup is enabled';
    return null;
  }

  function handleSave() {
    const err = validate();
    if (err) { setError(err); return; }
    onSave(form);
  }

  const isVmPool = form.clusterMode === 'vm-pool';

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2>{isNewFromExplorer ? 'Add Schedule' : isEdit ? 'Edit Cluster' : 'Add Cluster'}</h2>

        {error && <div className="form-error">{error}</div>}

        <div className="form-section-label">Cluster Mode</div>
        <div className="form-grid">
          <label style={{gridColumn:'span 2'}}>Mode
            <select value={form.clusterMode} onChange={e => update('clusterMode', e.target.value)}>
              <option value="gke">Native GKE Standard (scale node pools)</option>
              <option value="gke-autopilot">GKE Autopilot (scale workloads)</option>
              <option value="vm-pool">VM Pool (stop/start compute VMs)</option>
            </select>
          </label>
        </div>

        <div className="form-section-label">Cluster Details</div>
        <div className="form-grid">
          <label>ID (unique slug)
            <input value={form.id} onChange={e => update('id', e.target.value)} disabled={isEdit} placeholder="bofa-sandbox-ocp" />
          </label>
          <label>Display Name
            <input value={form.displayName} onChange={e => update('displayName', e.target.value)} placeholder="BofA Sandbox OCP" />
          </label>
          <label>GCP Project ID
            <input value={form.projectId} onChange={e => update('projectId', e.target.value)} placeholder="my-gcp-project" />
          </label>
          <label>{isVmPool ? 'VM Name Pattern (wildcards: * and ?)' : 'Cluster Name'}
            <input
              value={form.clusterName}
              onChange={e => update('clusterName', e.target.value)}
              placeholder={isVmPool ? 'e.g. ocp-worker-* or node-?-prod' : 'my-gke-cluster'}
            />
          </label>
          <label style={{gridColumn:'span 2'}}>Region
            <select value={form.region} onChange={e => update('region', e.target.value)}>
              <option value="">Select a region</option>
              {GCP_REGIONS.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </label>
        </div>

        <ResourcePreview
          projectId={form.projectId}
          region={form.region}
          pattern={form.clusterName}
          clusterMode={form.clusterMode}
        />

        <div className="form-section-label">Shutdown Schedule</div>
        <div className="form-grid">
          <label className="checkbox-label" style={{gridColumn:'span 2'}}>
            <input type="checkbox" checked={form.schedule.shutdownEnabled} onChange={e => update('schedule.shutdownEnabled', e.target.checked)} />
            Enable automatic shutdown
          </label>
          <label>Shutdown Cron
            <input value={form.schedule.shutdownCron} onChange={e => update('schedule.shutdownCron', e.target.value)} placeholder="0 17 * * *" disabled={!form.schedule.shutdownEnabled} />
          </label>
          <label>Timezone
            <select value={form.schedule.timezone} onChange={e => update('schedule.timezone', e.target.value)} disabled={!form.schedule.shutdownEnabled}>
              {TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="form-section-label">Startup Schedule</div>
        <div className="form-grid">
          <label className="checkbox-label" style={{gridColumn:'span 2'}}>
            <input type="checkbox" checked={form.schedule.startupEnabled} onChange={e => update('schedule.startupEnabled', e.target.checked)} />
            Enable automatic startup
          </label>
          <label>Startup Cron
            <input value={form.schedule.startupCron} onChange={e => update('schedule.startupCron', e.target.value)} placeholder="0 8 * * 1-5" disabled={!form.schedule.startupEnabled} />
          </label>
          <label>Timezone
            <select value={form.schedule.timezone} onChange={e => update('schedule.timezone', e.target.value)} disabled={!form.schedule.startupEnabled}>
              {TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="form-section-label">OpenShift Configuration</div>
        <div className="form-grid">
          <label className="checkbox-label" style={{gridColumn:'span 2'}}>
            <input type="checkbox" checked={form.openshift?.enabled || false} onChange={e => update('openshift.enabled', e.target.checked)} />
            This VM Pool runs an OpenShift cluster (enables safe ordered shutdown/startup)
          </label>
        </div>

        {form.openshift?.enabled && (
          <>
            <div className="form-grid">
              <label>OpenShift API URL
                <input value={form.openshift?.apiUrl || ''} onChange={e => update('openshift.apiUrl', e.target.value)} placeholder="https://api.cluster.domain.com:6443" style={{gridColumn:'span 2'}} />
              </label>
              <label>Service Account Token
                <input type="password" value={form.openshift?.token || ''} onChange={e => update('openshift.token', e.target.value)} placeholder="eyJhbGciOi..." />
              </label>
              <label>Drain Timeout (seconds)
                <input type="number" value={form.openshift?.drainTimeoutSeconds || 300} onChange={e => update('openshift.drainTimeoutSeconds', parseInt(e.target.value))} min="30" max="3600" />
              </label>
            </div>
            <div className="form-section-label">VM Role Patterns (keyword match in VM name)</div>
            <div className="form-grid">
              <label>Master keyword
                <input value={form.openshift?.masterPattern || ''} onChange={e => update('openshift.masterPattern', e.target.value)} placeholder="master" />
              </label>
              <label>Infra keyword
                <input value={form.openshift?.infraPattern || ''} onChange={e => update('openshift.infraPattern', e.target.value)} placeholder="infra" />
              </label>
              <label>Worker keyword
                <input value={form.openshift?.workerPattern || ''} onChange={e => update('openshift.workerPattern', e.target.value)} placeholder="worker" />
              </label>
              <div className="form-hint" style={{gridColumn:'span 1', fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-mono)', paddingTop:'4px'}}>
                Shutdown: workers → infra → masters<br/>
                Startup: masters → infra → workers
              </div>
            </div>
          </>
        )}

        <div className="modal-actions">
          <button className="btn btn-start" onClick={handleSave}>Save</button>
          <button className="btn btn-stop" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ScheduleInfo({ managed }) {
  if (!managed) return <span className="sched-badge sched-none">No schedule</span>;
  const { schedule } = managed;
  return (
    <span className="sched-info">
      {schedule?.shutdownEnabled
        ? <span className="sched-badge sched-shutdown" title={`Shutdown: ${schedule.shutdownCron} (${schedule.timezone})`}>↓ {schedule.shutdownCron}</span>
        : <span className="sched-badge sched-off">↓ off</span>
      }
      {schedule?.startupEnabled
        ? <span className="sched-badge sched-startup" title={`Startup: ${schedule.startupCron} (${schedule.timezone})`}>↑ {schedule.startupCron}</span>
        : <span className="sched-badge sched-off">↑ off</span>
      }
    </span>
  );
}

function GcpExplorer() {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);

  const [selectedProject, setSelectedProject] = useState(null);
  const [projectFilter, setProjectFilter] = useState('');

  const [clusters, setGkeClusters] = useState([]);
  const [clustersLoading, setClustersLoading] = useState(false);
  const [clustersError, setClustersError] = useState(null);

  const [vms, setVms] = useState([]);
  const [vmsLoading, setVmsLoading] = useState(false);
  const [vmsError, setVmsError] = useState(null);
  const [vmNameFilter, setVmNameFilter] = useState('');
  const [vmRegionFilter, setVmRegionFilter] = useState('');

  const [resourceTab, setResourceTab] = useState('clusters');
  const [expandedCluster, setExpandedCluster] = useState(null);

  const [scheduleModal, setScheduleModal] = useState(null);
  const [scheduleToast, setScheduleToast] = useState(null);

  // Managed clusters config from the backend - used to show schedule status
  const [managedClusters, setManagedClusters] = useState([]);

  useEffect(() => {
    apiFetch(`${API}/clusters`)
      .then(r => r.json())
      .then(d => setManagedClusters(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  // Reload managed clusters after saving a new schedule
  async function reloadManagedClusters() {
    try {
      const res = await apiFetch(`${API}/clusters`);
      setManagedClusters(await res.json());
    } catch {}
  }

  // Find the managed entry that matches a GKE cluster name + project
  function getManagedForGke(projectId, clusterName) {
    return managedClusters.find(m =>
      m.projectId === projectId &&
      m.clusterMode === 'gke' &&
      m.clusterName === clusterName
    ) || null;
  }

  // Find any managed entry whose vm-pool pattern matches this VM name + project
  function getManagedForVm(projectId, vmName) {
    return managedClusters.find(m => {
      if (m.projectId !== projectId || m.clusterMode !== 'vm-pool') return false;
      try {
        const escaped = m.clusterName
          .replace(/[.+^${}()|[\]\\]/g, '\\$&')
          .replace(/\*/g, '.*')
          .replace(/\?/g, '.');
        return new RegExp(`^${escaped}$`, 'i').test(vmName);
      } catch { return false; }
    }) || null;
  }

  const [fixingPermissions, setFixingPermissions] = useState(false);
  const [fixResult, setFixResult] = useState(null);

  async function fixPermissions(projectId) {
    setFixingPermissions(true);
    setFixResult(null);
    try {
      const res = await apiFetch(`${API}/permissions/${projectId}/grant`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setFixResult({ success: data.allGranted, message: data.allGranted ? 'Permissions granted. Retrying...' : 'Some permissions could not be granted.' });
      if (data.allGranted) {
        // Reload resources now that permissions are fixed
        setTimeout(() => {
          setFixResult(null);
          loadResources(projectId);
        }, 1500);
      }
    } catch (err) {
      setFixResult({ success: false, message: err.message });
    } finally {
      setFixingPermissions(false);
    }
  }

  function InlinePermissionError({ error, projectId }) {
    const isPermissionError = error && (error.includes('PERMISSION_DENIED') || error.includes('permission'));
    return (
      <div className="explorer-permission-error">
        <div className="explorer-permission-error-msg">{error}</div>
        {isPermissionError && (
          <div className="explorer-permission-error-actions">
            <button
              className="btn btn-fix"
              onClick={() => fixPermissions(projectId)}
              disabled={fixingPermissions}
            >
              {fixingPermissions ? <><span className="spinner-small" /> Granting...</> : '⚡ Fix Permissions'}
            </button>
            {fixResult && (
              <span className={`fix-result ${fixResult.success ? 'ok' : 'bad'}`}>
                {fixResult.message}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  function showScheduleToast(msg, type = 'info') {
    setScheduleToast({ msg, type });
    setTimeout(() => setScheduleToast(null), 4000);
  }

  function openScheduleForCluster(projectId, cluster) {
    const region = cluster.location.includes('/') ? cluster.location.split('/').pop() : cluster.location;
    setScheduleModal({
      prefill: {
        id: `${projectId}-${cluster.name}`.replace(/[^a-z0-9-]/gi, '-').toLowerCase(),
        displayName: `${cluster.name} (${projectId})`,
        projectId,
        clusterName: cluster.name,
        region,
        clusterMode: 'gke',
        schedule: {
          shutdownCron: '0 17 * * *',
          shutdownEnabled: true,
          startupCron: '0 8 * * 1-5',
          startupEnabled: false,
          timezone: 'America/Chicago',
        },
        tags: ['gke', projectId],
      },
    });
  }

  function openScheduleForVm(projectId, vm) {
    const region = vm.zone.split('-').slice(0, -1).join('-');
    setScheduleModal({
      prefill: {
        id: `${projectId}-${vm.name}`.replace(/[^a-z0-9-]/gi, '-').toLowerCase(),
        displayName: `${vm.name} (${projectId})`,
        projectId,
        clusterName: vm.name,
        region,
        clusterMode: 'vm-pool',
        schedule: {
          shutdownCron: '0 17 * * *',
          shutdownEnabled: true,
          startupCron: '0 8 * * 1-5',
          startupEnabled: false,
          timezone: 'America/Chicago',
        },
        tags: ['vm-pool', projectId],
      },
    });
  }

  async function handleScheduleSave(form) {
    const existingRes = await apiFetch(`${API}/clusters`);
    const existing = await existingRes.json();
    const isEdit = !!existing.find(c => c.id === form.id);
    const url = isEdit ? `${API}/clusters/${form.id}` : `${API}/clusters`;
    const method = isEdit ? 'PUT' : 'POST';
    try {
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        showScheduleToast(`Save failed: ${body.error || res.statusText}`, 'error');
        return;
      }
      setScheduleModal(null);
      await reloadManagedClusters();
      showScheduleToast(`Schedule added to dashboard`, 'success');
    } catch (err) {
      showScheduleToast(`Save failed: ${err.message}`, 'error');
    }
  }

  async function loadProjects() {
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const res = await apiFetch(`${API}/gcp/projects`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      setProjectsError(err.message);
    } finally {
      setProjectsLoading(false);
    }
  }

  async function loadResources(projectId) {
    setClustersError(null);
    setVmsError(null);
    setGkeClusters([]);
    setVms([]);

    setClustersLoading(true);
    try {
      const res = await apiFetch(`${API}/gcp/projects/${projectId}/clusters`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setGkeClusters(Array.isArray(data) ? data : []);
    } catch (err) {
      setClustersError(err.message);
      setGkeClusters([]);
    } finally {
      setClustersLoading(false);
    }

    setVmsLoading(true);
    try {
      const params = new URLSearchParams();
      if (vmRegionFilter) params.set('region', vmRegionFilter);
      if (vmNameFilter) params.set('filter', vmNameFilter);
      const res = await apiFetch(`${API}/gcp/projects/${projectId}/vms?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setVms(Array.isArray(data) ? data : []);
    } catch (err) {
      setVmsError(err.message);
      setVms([]);
    } finally {
      setVmsLoading(false);
    }
  }

  async function reloadVms() {
    if (!selectedProject) return;
    setVmsLoading(true);
    setVmsError(null);
    try {
      const params = new URLSearchParams();
      if (vmRegionFilter) params.set('region', vmRegionFilter);
      if (vmNameFilter) params.set('filter', vmNameFilter);
      const res = await apiFetch(`${API}/gcp/projects/${selectedProject}/vms?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setVms(Array.isArray(data) ? data : []);
    } catch (err) {
      setVmsError(err.message);
      setVms([]);
    } finally {
      setVmsLoading(false);
    }
  }

  function selectProject(projectId) {
    setSelectedProject(projectId);
    setExpandedCluster(null);
    loadResources(projectId);
  }

  const filteredProjects = projects.filter(p =>
    p.projectId.toLowerCase().includes(projectFilter.toLowerCase()) ||
    p.displayName.toLowerCase().includes(projectFilter.toLowerCase())
  );

  const VM_STATUS_COLOR = { RUNNING: '#00e5a0', TERMINATED: '#ff4757', STAGING: '#ffa502', STOPPING: '#ffa502' };

  return (
    <div className="explorer">
      <div className="explorer-sidebar">
        <div className="explorer-sidebar-header">
          <span className="explorer-sidebar-title">GCP Projects</span>
          <button className="btn btn-refresh" onClick={loadProjects} disabled={projectsLoading} style={{padding:'4px 8px',fontSize:'11px'}}>
            {projectsLoading ? <span className="spinner-small" /> : '↻'}
          </button>
        </div>
        {!projects.length && !projectsLoading && (
          <button className="btn btn-start" style={{margin:'12px',width:'calc(100% - 24px)'}} onClick={loadProjects}>
            Load Projects
          </button>
        )}
        {projectsError && <div className="explorer-error">{projectsError}</div>}
        <input
          className="explorer-search"
          placeholder="Filter projects..."
          value={projectFilter}
          onChange={e => setProjectFilter(e.target.value)}
        />
        <div className="explorer-project-list">
          {filteredProjects.map(p => (
            <div
              key={p.projectId}
              className={`explorer-project-item ${selectedProject === p.projectId ? 'active' : ''}`}
              onClick={() => selectProject(p.projectId)}
            >
              <span className="explorer-project-id">{p.projectId}</span>
              {p.displayName !== p.projectId && (
                <span className="explorer-project-name">{p.displayName}</span>
              )}
              <span className={`explorer-project-state ${p.state === 'ACTIVE' ? 'active' : 'inactive'}`}>
                {p.state}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="explorer-content">
        {!selectedProject ? (
          <div className="explorer-empty">Select a project to browse its resources.</div>
        ) : (
          <>
            <div className="explorer-content-header">
              <span className="explorer-content-title">{selectedProject}</span>
              <div className="explorer-tabs">
                <button
                  className={`explorer-tab ${resourceTab === 'clusters' ? 'active' : ''}`}
                  onClick={() => setResourceTab('clusters')}
                >
                  GKE Clusters {clustersLoading ? '' : `(${clusters.length})`}
                </button>
                <button
                  className={`explorer-tab ${resourceTab === 'vms' ? 'active' : ''}`}
                  onClick={() => setResourceTab('vms')}
                >
                  VM Instances {vmsLoading ? '' : `(${vms.length})`}
                </button>
              </div>
            </div>

            {resourceTab === 'clusters' && (
              <div className="explorer-resource-list">
                {clustersLoading && <div className="explorer-loading"><span className="spinner-small" /> Loading clusters...</div>}
                {clustersError && <InlinePermissionError error={clustersError} projectId={selectedProject} />}
                {!clustersLoading && !clustersError && clusters.length === 0 && (
                  <div className="explorer-empty">No GKE clusters found in this project.</div>
                )}
                {clusters.map(c => (
                  <div key={c.name} className="explorer-resource-card">
                    <div className="explorer-resource-header" onClick={() => setExpandedCluster(expandedCluster === c.name ? null : c.name)}>
                      <div>
                        <span className="explorer-resource-name">{c.name}</span>
                        <span className="explorer-resource-meta">{c.location}</span>
                      </div>
                      <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap',justifyContent:'flex-end'}}>
                        <ScheduleInfo managed={getManagedForGke(selectedProject, c.name)} />
                        <span className="badge badge-gray">{c.currentMasterVersion}</span>
                        <span className="badge badge-gray">{c.currentNodeCount} nodes</span>
                        <span className={`badge ${c.status === 'RUNNING' ? 'badge-green' : 'badge-gray'}`}>{c.status}</span>
                        <button
                          className="btn btn-schedule"
                          onClick={e => { e.stopPropagation(); openScheduleForCluster(selectedProject, c); }}
                        >
                          {getManagedForGke(selectedProject, c.name) ? '✎ Schedule' : '+ Schedule'}
                        </button>
                        <span style={{color:'var(--text-muted)',fontSize:'11px'}}>{expandedCluster === c.name ? '▲' : '▼'}</span>
                      </div>
                    </div>
                    {expandedCluster === c.name && c.nodePools.length > 0 && (
                      <div className="explorer-node-pools">
                        <div className="explorer-node-pool-header">
                          <span>Pool Name</span><span>Nodes</span><span>Machine Type</span><span>Status</span>
                        </div>
                        {c.nodePools.map(p => (
                          <div key={p.name} className="explorer-node-pool-row">
                            <span>{p.name}</span>
                            <span>{p.nodeCount}</span>
                            <span>{p.machineType || '—'}</span>
                            <span>{p.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {resourceTab === 'vms' && (
              <div className="explorer-resource-list">
                <div className="explorer-vm-filters">
                  <input
                    className="explorer-search"
                    style={{flex:1}}
                    placeholder="Name filter (wildcards * and ? supported)"
                    value={vmNameFilter}
                    onChange={e => setVmNameFilter(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && reloadVms()}
                  />
                  <input
                    className="explorer-search"
                    style={{width:'160px'}}
                    placeholder="Region (e.g. us-central1)"
                    value={vmRegionFilter}
                    onChange={e => setVmRegionFilter(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && reloadVms()}
                  />
                  <button className="btn btn-refresh" onClick={reloadVms} disabled={vmsLoading}>
                    {vmsLoading ? <span className="spinner-small" /> : '↻'} Apply
                  </button>
                </div>
                {vmsLoading && <div className="explorer-loading"><span className="spinner-small" /> Loading VMs...</div>}
                {vmsError && <InlinePermissionError error={vmsError} projectId={selectedProject} />}
                {!vmsLoading && !vmsError && vms.length === 0 && (
                  <div className="explorer-empty">No VM instances found. Try adjusting the filters.</div>
                )}
                {vms.length > 0 && (
                  <div className="explorer-vm-table">
                    <div className="explorer-vm-header">
                      <span>Name</span><span>Zone</span><span>Machine</span><span>Internal IP</span><span>Status</span><span>Schedule</span><span></span>
                    </div>
                    {vms.map(vm => {
                      const managed = getManagedForVm(selectedProject, vm.name);
                      return (
                        <div key={`${vm.zone}-${vm.name}`} className="explorer-vm-row">
                          <span className="explorer-vm-name">{vm.name}</span>
                          <span>{vm.zone}</span>
                          <span>{vm.machineType || '—'}</span>
                          <span>{vm.networkInterfaces?.[0]?.networkIP || '—'}</span>
                          <span style={{color: VM_STATUS_COLOR[vm.status] || 'var(--text-muted)'}}>{vm.status}</span>
                          <span><ScheduleInfo managed={managed} /></span>
                          <span>
                            <button
                              className="btn btn-schedule"
                              style={{padding:'3px 8px',fontSize:'11px'}}
                              onClick={() => openScheduleForVm(selectedProject, vm)}
                            >
                              {managed ? '✎ Schedule' : '+ Schedule'}
                            </button>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {scheduleModal && (
        <ClusterForm
          cluster={scheduleModal.prefill}
          onSave={handleScheduleSave}
          onCancel={() => setScheduleModal(null)}
          isNewFromExplorer
        />
      )}

      {scheduleToast && (
        <div className={`toast toast-${scheduleToast.type}`}>{scheduleToast.msg}</div>
      )}
    </div>
  );
}

function PermissionsPanel() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [grantingProject, setGrantingProject] = useState(null);
  const [customProject, setCustomProject] = useState('');
  const [error, setError] = useState(null);

  async function checkPermissions(projectId) {
    setLoading(true);
    setError(null);
    try {
      const url = projectId
        ? `${API}/permissions/${projectId}`
        : `${API}/permissions`;
      const res = await apiFetch(url);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setResults(projectId ? [data] : data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function grantPermissions(projectId) {
    setGrantingProject(projectId);
    try {
      const res = await apiFetch(`${API}/permissions/${projectId}/grant`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      // Refresh the check for this project
      const checkRes = await apiFetch(`${API}/permissions/${projectId}`);
      const checkData = await checkRes.json();
      setResults(prev => prev.map(r => r.projectId === projectId ? checkData : r));
    } catch (err) {
      setError(err.message);
    } finally {
      setGrantingProject(null);
    }
  }

  const ROLE_ICON = { true: '✓', false: '✗' };

  return (
    <div className="permissions-panel">
      <div className="permissions-header">
        <div>
          <h2 className="permissions-title">IAM Permissions</h2>
          <p className="permissions-sub">Check and fix service account permissions across your GCP projects.</p>
        </div>
        <div className="permissions-actions">
          <button className="btn btn-refresh" onClick={() => checkPermissions()} disabled={loading}>
            {loading ? <span className="spinner-small" /> : '↻'} Check All Managed Projects
          </button>
        </div>
      </div>

      <div className="permissions-custom">
        <input
          className="explorer-search"
          style={{flex:1, maxWidth:'320px'}}
          placeholder="Check a specific project ID..."
          value={customProject}
          onChange={e => setCustomProject(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && customProject && checkPermissions(customProject)}
        />
        <button
          className="btn btn-refresh"
          onClick={() => customProject && checkPermissions(customProject)}
          disabled={loading || !customProject}
        >
          Check Project
        </button>
      </div>

      {error && <div className="explorer-error" style={{margin:'0 0 16px'}}>{error}</div>}

      {results.length === 0 && !loading && (
        <div className="permissions-empty">
          Click "Check All Managed Projects" to audit permissions, or enter a specific project ID above.
        </div>
      )}

      {results.map(r => (
        <div key={r.projectId} className={`permissions-card ${r.allGranted ? 'all-ok' : 'has-missing'}`}>
          <div className="permissions-card-header">
            <div>
              <span className="permissions-project-id">{r.projectId}</span>
              {r.serviceAccount && (
                <span className="permissions-sa">{r.serviceAccount}</span>
              )}
            </div>
            <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
              {r.error ? (
                <span className="badge badge-red">Error</span>
              ) : r.allGranted ? (
                <span className="badge badge-green">All permissions granted</span>
              ) : (
                <span className="badge badge-red">Missing permissions</span>
              )}
              {!r.allGranted && !r.error && (
                <button
                  className="btn btn-fix"
                  onClick={() => grantPermissions(r.projectId)}
                  disabled={grantingProject === r.projectId}
                >
                  {grantingProject === r.projectId ? <><span className="spinner-small" /> Granting...</> : '⚡ Fix Permissions'}
                </button>
              )}
            </div>
          </div>

          {r.error ? (
            <div className="permissions-error-detail">{r.error}</div>
          ) : (
            <div className="permissions-role-table">
              <div className="permissions-role-header">
                <span>Role</span>
                <span>Purpose</span>
                <span>Status</span>
              </div>
              {r.roles?.map(role => (
                <div key={role.role} className={`permissions-role-row ${role.granted ? 'granted' : 'missing'}`}>
                  <span>
                    <span className="role-label">{role.label}</span>
                    <span className="role-id">{role.role}</span>
                  </span>
                  <span className="role-reason">{role.reason}</span>
                  <span className={`role-status ${role.granted ? 'ok' : 'bad'}`}>
                    {ROLE_ICON[role.granted]} {role.granted ? 'Granted' : 'Missing'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AppDashboard({ authUser, onLogout }) {
  const [clusters, setClusters] = useState([]);
  const [modal, setModal] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }

  async function loadClusters() {
    try {
      const res = await apiFetch(`${API}/clusters`);
      if (res.status === 401) { onLogout(); return; }
      setClusters(await res.json());
    } catch {
      showToast('Failed to load clusters', 'error');
    }
  }

  useEffect(() => { loadClusters(); }, []);

  // Refresh token every 7 days to keep session alive (token expires in 30d)
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await apiFetch(`${API}/auth/refresh`, { method: 'POST' });
        if (res.ok) {
          const data = await res.json();
          setToken(data.token);
        } else {
          onLogout();
        }
      } catch { onLogout(); }
    }, 7 * 24 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  async function handleStopAll() {
    setGlobalLoading(true);
    try {
      await apiFetch(`${API}/clusters/all/stop`, { method: 'POST' });
      showToast('Stop signal sent to all clusters', 'success');
    } catch {
      showToast('Failed to stop all clusters', 'error');
    } finally {
      setGlobalLoading(false);
    }
  }

  async function handleSave(form) {
    const isEdit = !!clusters.find(c => c.id === form.id);
    const url = isEdit ? `${API}/clusters/${form.id}` : `${API}/clusters`;
    const method = isEdit ? 'PUT' : 'POST';
    try {
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        showToast(`Save failed: ${body.error || res.statusText}`, 'error');
        return;
      }
      setModal(null);
      await loadClusters();
      showToast(isEdit ? 'Cluster updated' : 'Cluster added', 'success');
    } catch (err) {
      showToast(`Save failed: ${err.message}`, 'error');
    }
  }

  async function handleDelete(cluster) {
    if (!window.confirm(`Remove ${cluster.displayName} from this manager?`)) return;
    await apiFetch(`${API}/clusters/${cluster.id}`, { method: 'DELETE' });
    loadClusters();
    showToast('Cluster removed', 'info');
  }

  function handleCardAction(action, cluster) {
    if (action === 'edit') setModal({ type: 'edit', cluster });
    if (action === 'delete') handleDelete(cluster);
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <span className="header-logo">GCP</span>
          <div>
            <h1>Cluster Manager</h1>
            <p className="header-sub">tam-aaron-hubbart</p>
          </div>
          <nav className="header-nav">
            <button className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>Dashboard</button>
            <button className={`nav-tab ${activeTab === 'explorer' ? 'active' : ''}`} onClick={() => setActiveTab('explorer')}>GCP Explorer</button>
            <button className={`nav-tab ${activeTab === 'permissions' ? 'active' : ''}`} onClick={() => setActiveTab('permissions')}>Permissions</button>
          </nav>
        </div>
        <div className="header-actions">
          <button
            className={`btn btn-debug btn-large ${debugMode ? 'active' : ''}`}
            onClick={() => setDebugMode(d => !d)}
            title="Toggle debug mode"
          >
            ⚙ Debug {debugMode ? 'ON' : 'OFF'}
          </button>
          {activeTab === 'dashboard' && (
            <>
              <button className="btn btn-stop btn-large" onClick={handleStopAll} disabled={globalLoading}>
                {globalLoading ? <span className="spinner-small" /> : '■'} Stop All
              </button>
              <button className="btn btn-start btn-large" onClick={() => setModal({ type: 'add' })}>
                + Add Cluster
              </button>
            </>
          )}
          <div className="header-user">
            <span className="header-username">{authUser}</span>
            <button className="btn btn-delete" onClick={onLogout} style={{padding:'6px 12px'}}>Sign Out</button>
          </div>
        </div>
      </header>

      {activeTab === 'dashboard' && (
        <main className="app-main">
          <div className="stats-bar">
            <div className="stat">
              <span className="stat-value">{clusters.length}</span>
              <span className="stat-label">Clusters</span>
            </div>
            <div className="stat">
              <span className="stat-value">{clusters.filter(c => c.schedule?.shutdownEnabled || c.schedule?.startupEnabled).length}</span>
              <span className="stat-label">Scheduled</span>
            </div>
            <div className="stat">
              <span className="stat-value">5:00 PM CT</span>
              <span className="stat-label">Daily Shutdown</span>
            </div>
          </div>
          <div className="cluster-grid">
            {clusters.length === 0 && (
              <div className="empty-state">No clusters configured. Add one to get started.</div>
            )}
            {clusters.map(cluster => (
              <ClusterCard key={cluster.id} cluster={cluster} onAction={handleCardAction} debugMode={debugMode} />
            ))}
          </div>
        </main>
      )}

      {activeTab === 'explorer' && (
        <main className="app-main app-main-explorer">
          <GcpExplorer />
        </main>
      )}

      {activeTab === 'permissions' && (
        <main className="app-main">
          <PermissionsPanel />
        </main>
      )}

      {modal?.type === 'add' && (
        <ClusterForm onSave={handleSave} onCancel={() => setModal(null)} />
      )}
      {modal?.type === 'edit' && (
        <ClusterForm cluster={modal.cluster} onSave={handleSave} onCancel={() => setModal(null)} />
      )}

      {toast && (
        <div className={`toast toast-${toast.type}`}>{toast.msg}</div>
      )}
    </div>
  );
}

export default function App() {
  const storedToken = getToken();
  let initialUser = null;
  if (storedToken) {
    try {
      const payload = JSON.parse(atob(storedToken.split('.')[1]));
      // Only reject if exp exists AND is clearly in the past
      const expired = payload.exp && (payload.exp * 1000 < Date.now());
      if (!expired) {
        initialUser = payload.username || 'user';
      } else {
        clearToken();
      }
    } catch { clearToken(); }
  }

  const [authed, setAuthed] = useState(!!initialUser);
  const [authUser, setAuthUser] = useState(initialUser);

  function handleLogin(token) {
    setToken(token);
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setAuthUser(payload.username || 'user');
    } catch { setAuthUser('user'); }
    setAuthed(true);
  }

  function handleLogout() {
    clearToken();
    setAuthed(false);
    setAuthUser(null);
  }

  if (!authed) return <LoginScreen onLogin={handleLogin} />;
  return <AppDashboard authUser={authUser} onLogout={handleLogout} />;
}
